import type {
  AnalysisDetail,
  AnalysisPage,
  AnalyzeInput,
  AuthUser,
  HistoryQuery,
  LoginResponse,
  V1AnalyzeResponse,
} from './types'
import { CSRF_HEADER_NAME, notifyUnauthorized, readCsrfToken } from '../auth/session'

/**
 * Cliente HTTP de la Product API. Es el ÚNICO punto de contacto frontend↔backend:
 * el frontend nunca conoce el conector ni la base; solo intercambia JSON.
 *
 * SEC-001 — La autenticación ya no se adjunta aquí. Toda petición viaja con
 * `credentials: "include"` para que el navegador envíe la cookie de sesión `HttpOnly`,
 * que este código **no puede leer**. Lo único que el cliente añade a mano es el token
 * CSRF, y solo en las operaciones mutables. No existe ninguna variable que contenga el
 * JWT: no hay nada que un XSS pueda robar de este módulo.
 */

/**
 * Base de la API. Vacía por defecto = **rutas relativas del mismo origen**.
 *
 * DEPLOY-001A Fase 3 — En Release Candidate el frontend y la API se sirven bajo el mismo
 * origen HTTPS: `/api/*` lo resuelve el reverse proxy que sirve este mismo bundle. Una
 * base vacía produce peticiones a `/api/...`, que el navegador resuelve contra el origen
 * de la página.
 *
 * El valor anterior era `http://localhost:8001` como predeterminado, lo que hacía que un
 * bundle construido sin `VITE_API_BASE_URL` apuntara al puerto local del *usuario* en
 * producción — la aplicación quedaba rota sin ningún error de construcción que lo
 * delatara. Ahora el desarrollo debe declarar su base explícitamente y producción no
 * necesita declarar ninguna.
 *
 * Con el mismo origen, además, las cookies de sesión viajan sin necesidad de CORS entre
 * orígenes, y `SameSite=Lax` protege sin excepciones.
 */
const API_BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

export type ApiErrorKind = 'network' | 'http' | 'parse'

export class ApiError extends Error {
  kind: ApiErrorKind
  status?: number

  constructor(message: string, kind: ApiErrorKind, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.kind = kind
    this.status = status
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Ruta protegida: un 401 debe cerrar la sesión del cliente. */
  auth?: boolean
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  // Doble envío CSRF: solo en operaciones mutables. En las seguras no aporta nada y en
  // el preflight de CORS estorbaría.
  if (!SAFE_METHODS.has(method.toUpperCase())) {
    const csrf = readCsrfToken()
    if (csrf) headers[CSRF_HEADER_NAME] = csrf
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      // Sin esto el navegador no envía la cookie de sesión entre orígenes y toda ruta
      // protegida respondería 401. Es lo que sustituye a la cabecera Authorization.
      credentials: 'include',
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })
  } catch {
    throw new ApiError('No se pudo conectar con el servidor de AGOS.', 'network')
  }

  if (response.status === 401) {
    // Sesión inválida/expirada en una ruta protegida → cerrar sesión.
    if (options.auth) notifyUnauthorized()
    throw new ApiError('Sesión no válida o expirada.', 'http', 401)
  }
  if (response.status === 403) {
    throw new ApiError('No tienes permisos para esta acción.', 'http', 403)
  }
  if (response.status === 422) {
    throw new ApiError('La solicitud no es válida. Revisa los datos.', 'http', 422)
  }
  if (response.status === 404) {
    throw new ApiError('No se encontró el recurso solicitado.', 'http', 404)
  }
  if (!response.ok) {
    throw new ApiError(`El servidor respondió con un error (${response.status}).`, 'http', response.status)
  }

  try {
    return (await response.json()) as T
  } catch {
    throw new ApiError('La respuesta del servidor no pudo interpretarse.', 'parse')
  }
}

// --- auth ------------------------------------------------------------------
export async function apiLogin(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/api/auth/login', { method: 'POST', body: { email, password } })
}

export async function apiLogout(): Promise<void> {
  try {
    await request<unknown>('/api/auth/logout', { method: 'POST' })
  } catch {
    // Best-effort: si el servidor no responde, el estado local se limpia igualmente. La
    // sesión sigue siendo revocable en el servidor y vence por sí sola.
  }
}

export async function apiMe(): Promise<AuthUser> {
  return request<AuthUser>('/api/auth/me', { auth: true })
}

// --- análisis (protegido) --------------------------------------------------
export async function analyzeCompany(input: AnalyzeInput): Promise<V1AnalyzeResponse> {
  return request<V1AnalyzeResponse>('/api/v1/analyze/company', {
    method: 'POST',
    body: input,
    auth: true,
  })
}

export async function listAnalyses(query: HistoryQuery = {}): Promise<AnalysisPage> {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value))
    }
  }
  const qs = params.toString()
  return request<AnalysisPage>(`/api/v1/analyses${qs ? `?${qs}` : ''}`, { auth: true })
}

export async function getAnalysis(analysisId: string): Promise<AnalysisDetail> {
  return request<AnalysisDetail>(`/api/v1/analyses/${encodeURIComponent(analysisId)}`, {
    auth: true,
  })
}
